/**
 * Fabrique des blocs CMS avancé de la categorie "shell" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { FooterContentPreview, HeaderBasketPreview, HeaderCtaPreview, HeaderLanguagePreview, HeaderUserAuthPreview, SiteLogoPreview, SiteMenuPreview, SiteMenuPreviewEditing } from "@/components/cms-puck/previews/ShellFlowPreviews";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { type RawComponentsConfig } from "../types";

export function buildShellBlocks() {
  return {
    SiteLogo: {
      label: cmsNew("cms.new.auto.label.logo_du_site", "Logo du site"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.en_tete_pied_de_page",
            "En-tête & pied de page",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.siteLogo",
                "Titre et sous-titre du site (lien vers l'accueil), repris des réglages d'instance. Rien à saisir ici.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: () => <SiteLogoPreview />,
    },

    SiteMenu: {
      label: cmsNew("cms.new.auto.label.menu", "Menu"),
      fields: {
        menuKey: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.menu", "Menu"),
          options: [
            {
              label: cmsNew(
                "cms.new.auto.label.principal_header",
                "Principal (header)",
              ),
              value: "primary",
            },
            {
              label: cmsNew(
                "cms.new.auto.label.pied_de_page",
                "Pied de page",
              ),
              value: "footer",
            },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centre"),
              value: "center",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        info: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.menus", "Menus"),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.siteMenu.pre",
                "Configurez les entrées dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() => window.open("/cms/advanced/menus", "_blank")}
              >
                {cmsNew("cms.new.hint.link.menus", "Menus")}
              </button>
              {cmsNew(
                "cms.new.hint.siteMenu.post",
                ". L'aperçu et le site public utilisent le menu résolu pour la clé choisie.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { menuKey: "primary", align: "left", info: null },
      // `usePuck()` n'existe que dans l'éditeur (cf. LayoutSlot) : hors édition
      // (aperçu `<Render>`), pas de lecture des boutons de l'en-tête.
      render: ({ menuKey, align, puck }) =>
        puck?.isEditing ? (
          <SiteMenuPreviewEditing menuKey={menuKey} align={align} />
        ) : (
          <SiteMenuPreview menuKey={menuKey} align={align} />
        ),
    },

    HeaderCta: {
      label: cmsNew("cms.new.auto.label.boutons_cta_en_tete", "Boutons CTA (en-tête)"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.en_tete_pied_de_page",
            "En-tête & pied de page",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.headerCta",
                "Boutons « Catalogue raisonné » et « Boutique » repris des réglages d'instance (affichés si activés). Rien à saisir ici.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: () => <HeaderCtaPreview />,
    },

    HeaderUserAuth: {
      label: cmsNew(
        "cms.new.auto.label.profil_connexion",
        "Profil / Connexion",
      ),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.en_tete_pied_de_page",
            "En-tête & pied de page",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.headerUserAuth",
                "Menu compte connecté ou lien de connexion selon l'état visiteur. Visible si l'espace membre ou la boutique est activée.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: () => <HeaderUserAuthPreview />,
    },

    HeaderBasket: {
      label: cmsNew("cms.new.blocks.headerBasket.label", "Panier"),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.en_tete_pied_de_page",
            "En-tête & pied de page",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.headerBasket",
                "Icône panier avec badge si la boutique est activée et que le panier contient des articles.",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: () => <HeaderBasketPreview />,
    },

    HeaderLanguage: {
      label: cmsNew(
        "cms.new.auto.label.selecteur_de_langue",
        "Sélecteur de langue",
      ),
      fields: {
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.en_tete_pied_de_page",
            "En-tête & pied de page",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.headerLanguage",
                "Liste déroulante des langues actives du site (masqué si une seule langue).",
              )}
            </p>
          ),
        },
      },
      defaultProps: { info: null },
      render: () => <HeaderLanguagePreview />,
    },

    FooterContent: {
      label: cmsNew(
        "cms.new.blocks.footerContent.label",
        "Contenu pied de page",
      ),
      fields: {
        showArchives: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.archives", "Archives"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.afficher", "Afficher"),
              value: true,
            },
            {
              label: cmsNew("cms.new.auto.label.masquer", "Masquer"),
              value: false,
            },
          ],
        },
        info: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.en_tete_pied_de_page",
            "En-tête & pied de page",
          ),
          render: () => (
            <p className="p-1 text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.footerContent.pre",
                "Réseaux sociaux, mentions légales et archives. Assignez le template dans",
              )}{" "}
              <button
                type="button"
                className="font-medium text-slate-700 underline"
                onClick={() =>
                  window.open("/cms/advanced/pied-de-page", "_blank")
                }
              >
                {cmsNew("cms.new.hint.link.footer", "Pied de page")}
              </button>
              .
            </p>
          ),
        },
      },
      defaultProps: { showArchives: true, info: null },
      render: ({ showArchives }) => (
        <FooterContentPreview showArchives={showArchives} />
      ),
    },
  } satisfies Pick<RawComponentsConfig, "SiteLogo" | "SiteMenu" | "HeaderCta" | "HeaderUserAuth" | "HeaderBasket" | "HeaderLanguage" | "FooterContent">;
}
